/**
 * "What is being collected about me, and how is it judged" — spec 01 §17's first two
 * user stories, and the transparency principle of Master §13.3: *a person scored on
 * a rule they cannot read is what that section exists to prevent.*
 *
 * `GET /api/v1/activities/catalog?on_date=D` returns the company defaults plus **this
 * employee's own** department and job-title overrides, and no other department's. Any
 * `view_*` reaches it — a deliberately lower bar than the `manage_policies` the admin
 * catalogue needs, and `openapi.yaml` says why in as many words.
 *
 * ── Why not `GET /activities/applications` ─────────────────────────────────
 *
 * Because it requires `workforce.view_apps`, which an employee does not hold. Calling
 * it here would render a permission refusal on an employee's own screen — the one
 * screen spec 01 §17 requires to keep working. The usage rollup is a manager's view
 * of a person; the catalogue is the person's view of the rules. Different question,
 * different endpoint, different permission, and they are not substitutes.
 *
 * ── The private list is the point of this panel ────────────────────────────
 *
 * `WebsiteCatalogEntry.is_private` marks a domain the company has decided is **never
 * named**. Time on it is still counted — Task 35 records it as time that was worked
 * with no name, Task 36 counts it in `online`/`active` and `private_seconds` — so the
 * panel says exactly that, and the private rules are listed first. An employee who
 * cannot see which domains are private cannot tell whether the promise is being kept.
 *
 * ── The half of §17 this build cannot serve, stated ────────────────────────
 *
 * "Policy có version, effective date và acknowledgement": the DESKTOP policy version
 * and its acknowledgement travel over `GET /api/v1/agent/policy` and
 * `POST /api/v1/agent/policy-ack`, both **device-authenticated** — the Agent
 * acknowledges, and shows the version, level and private lists in its tray. So this
 * panel names them and offers no acknowledgement button for the desktop policy,
 * exactly as `web/shapes/select-company.md` §2 ruled for `switch-company`: a button
 * that cannot acknowledge anything is worse than a sentence saying where
 * acknowledgement happens.
 *
 * **What this panel used to say, and no longer does (Task U12).** It said there was
 * "no session endpoint for either", which read as "nothing in this service records
 * that you were told anything". Since migration 055 something does:
 * `wp_employee_consents` holds one row per employee per notice version, and for
 * LOCATION monitoring both halves have session endpoints — `POST
 * /api/v1/geo/my-location-notice` records the acknowledgment and `POST
 * /api/v1/geo/my-location-opt-out` withdraws it while stopping GPS capture outright.
 * The lawful basis both rest on is named on this screen too, in the PDPL's own
 * enumerated terms; "legitimate interest" is a GDPR balancing test the PDPL does not
 * enumerate and appears nowhere (plan KTD11).
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import { catalogPath } from '../../api/paths';
import type {
  ActivityCategory,
  EffectiveCatalogResponse,
  ProductivityClass,
  WebsiteCatalogEntry,
} from '../../api/types';
import { useApiQuery } from '../../api/useApiQuery';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { Badge } from '../../states/Badge';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';
import type { Severity } from '../../states/icons';

/**
 * The fifteen categories of Master §9.1, and the three rungs of the override ladder.
 *
 * Exhaustive `Record`s rather than template-built keys: `Record<ActivityCategory,
 * MessageKey>` fails to compile if a sixteenth category is ever added to the wire
 * type without a string to render it, whereas `` `catalog.category.${category}` ``
 * would compile and put a raw key on screen.
 */
const CATEGORY_KEY: Readonly<Record<ActivityCategory, MessageKey>> = {
  productive: 'catalog.category.productive',
  neutral: 'catalog.category.neutral',
  unproductive: 'catalog.category.unproductive',
  communication: 'catalog.category.communication',
  meeting: 'catalog.category.meeting',
  development: 'catalog.category.development',
  design: 'catalog.category.design',
  accounting: 'catalog.category.accounting',
  sales: 'catalog.category.sales',
  marketing: 'catalog.category.marketing',
  research: 'catalog.category.research',
  training: 'catalog.category.training',
  system: 'catalog.category.system',
  blocked: 'catalog.category.blocked',
  uncategorized: 'catalog.category.uncategorized',
};

const SCOPE_KEY: Readonly<Record<'company' | 'department' | 'job_title', MessageKey>> = {
  company: 'catalog.scope.company',
  department: 'catalog.scope.department',
  job_title: 'catalog.scope.jobTitle',
};

/**
 * The four productivity classes, and `null`.
 *
 * `null` is a **value**: eleven of the fifteen categories make no productivity claim
 * at all, and `openapi.yaml` says that is deliberate, not a gap. It renders as "không
 * xếp loại năng suất" rather than as a blank cell, because a blank reads as an
 * oversight and this one is a decision.
 */
function classBadge(value: ProductivityClass | undefined): {
  severity: Severity;
  icon: 'circle-check' | 'circle-alert' | 'info';
  key: MessageKey;
} {
  switch (value) {
    case 'productive':
      return { severity: 'low', icon: 'circle-check', key: 'catalog.class.productive' };
    case 'unproductive':
      return { severity: 'medium', icon: 'circle-alert', key: 'catalog.class.unproductive' };
    case 'neutral':
      return { severity: 'info', icon: 'info', key: 'catalog.class.neutral' };
    case 'unclassified':
      return { severity: 'info', icon: 'info', key: 'catalog.class.unclassified' };
    default:
      return { severity: 'info', icon: 'info', key: 'catalog.class.none' };
  }
}

export function CollectionRules({ date }: { readonly date: string }): ReactNode {
  const { t, locale } = useI18n();
  const { status, data, error, reload } = useApiQuery<EffectiveCatalogResponse>(catalogPath(date));

  const privateSites: readonly WebsiteCatalogEntry[] =
    data === null ? [] : data.websites.filter((site) => site.is_private === true);
  const namedSites: readonly WebsiteCatalogEntry[] =
    data === null ? [] : data.websites.filter((site) => site.is_private !== true);
  const empty = data !== null && data.applications.length === 0 && data.websites.length === 0;

  return (
    <section className="wp-section" aria-labelledby="rules-heading">
      <h2 className="wp-section__title" id="rules-heading">
        {t('catalog.heading')}
      </h2>
      <p className="wp-small wp-muted">
        {t('catalog.intro', {
          date: formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
        })}
      </p>

      {status === 'loading' ? (
        <Skeleton rows={4} label={t('catalog.loading')} testId="catalog-skeleton" />
      ) : null}

      {status === 'error' && error !== null ? (
        <ApiErrorNote
          error={error}
          onRetry={reload}
          subject={t('catalog.subject')}
          testId="catalog-error"
        />
      ) : null}

      {empty ? (
        // A fact about the company's configuration, never about the person.
        <EmptyState
          title={t('catalog.empty.title')}
          description={t('catalog.empty.body')}
          testId="catalog-empty"
        />
      ) : null}

      {data === null ? null : (
        <>
          <h3 className="wp-section__title">{t('catalog.private.heading')}</h3>
          {privateSites.length === 0 ? (
            <p className="wp-muted" data-testid="catalog-private-none">
              {t('catalog.private.none')}
            </p>
          ) : (
            <ul className="wp-rows" data-testid="catalog-private">
              {privateSites.map((site) => (
                <li key={site.id} className="wp-row">
                  <span className="wp-row__main wp-mono">{site.match_value}</span>
                  <Badge
                    severity="low"
                    icon="lock"
                    label={t('catalog.private.badge')}
                    title={t('catalog.private.help')}
                    testId={`catalog-private-${site.id}`}
                  />
                </li>
              ))}
            </ul>
          )}
          <p className="wp-small wp-muted">{t('catalog.private.body')}</p>

          <h3 className="wp-section__title">{t('catalog.apps.heading')}</h3>
          {data.applications.length === 0 ? (
            <p className="wp-muted" data-testid="catalog-apps-none">
              {t('catalog.apps.none')}
            </p>
          ) : (
            <ul className="wp-rows" data-testid="catalog-apps">
              {data.applications.map((app) => {
                const badge = classBadge(app.productivity_class);
                return (
                  <li key={app.id} className="wp-row">
                    <span className="wp-row__main">
                      <span className="wp-mono">{app.match_value}</span>
                      {app.display_name === null || app.display_name === undefined
                        ? null
                        : ` · ${app.display_name}`}
                    </span>
                    <span className="wp-flags">
                      <Badge
                        severity="info"
                        icon="info"
                        label={t(CATEGORY_KEY[app.category])}
                        testId={`catalog-app-category-${app.id}`}
                      />
                      <Badge
                        severity={badge.severity}
                        icon={badge.icon}
                        label={t(badge.key)}
                        testId={`catalog-app-class-${app.id}`}
                      />
                      <Badge
                        severity="info"
                        icon="info"
                        label={t(SCOPE_KEY[app.scope_type])}
                        title={t('catalog.scope.help')}
                        testId={`catalog-app-scope-${app.id}`}
                      />
                    </span>
                  </li>
                );
              })}
            </ul>
          )}

          <h3 className="wp-section__title">{t('catalog.sites.heading')}</h3>
          {namedSites.length === 0 ? (
            <p className="wp-muted" data-testid="catalog-sites-none">
              {t('catalog.sites.none')}
            </p>
          ) : (
            <ul className="wp-rows" data-testid="catalog-sites">
              {namedSites.map((site) => {
                const badge = classBadge(site.productivity_class);
                return (
                  <li key={site.id} className="wp-row">
                    <span className="wp-row__main wp-mono">{site.match_value}</span>
                    <span className="wp-flags">
                      <Badge
                        severity="info"
                        icon="info"
                        label={t(CATEGORY_KEY[site.category])}
                        testId={`catalog-site-category-${site.id}`}
                      />
                      <Badge
                        severity={badge.severity}
                        icon={badge.icon}
                        label={t(badge.key)}
                        testId={`catalog-site-class-${site.id}`}
                      />
                      {site.is_blacklisted === true ? (
                        <Badge
                          severity="medium"
                          icon="ban"
                          label={t('catalog.blacklisted')}
                          title={t('catalog.blacklisted.help')}
                          testId={`catalog-site-blocked-${site.id}`}
                        />
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <StatusNote severity="info" title={t('policy.heading')} testId="policy-panel">
        <p>{t('policy.body')}</p>
        {/*
          Task U12 — `policy.endpoints` used to end "so this page cannot read them
          and does not offer an acknowledgement button", which read as "nothing in
          this service records that you were told". Since migration 055 something
          does: `wp_employee_consents` holds one row per employee per notice
          version, and for location monitoring the acknowledgment and its
          withdrawal both have session endpoints. The desktop half is still the
          Agent's device-signed path, and the sentence now says which is which
          instead of denying both.
        */}
        <p className="wp-small wp-muted">{t('policy.endpoints')}</p>
        <p data-testid="policy-lawful-basis">{t('policy.lawfulBasis')}</p>
        <p className="wp-small wp-muted" data-testid="policy-consent-record">
          {t('policy.consentRecord')}
        </p>
        {/* Was "… của chính bạn (mã nhân viên 1)". The reader already knows who they
            are; the id was a machine's word in a person's sentence (Luật Không In Mã
            Máy, R3), and it named nothing this screen does not otherwise say. */}
        <p className="wp-small wp-muted">{t('policy.self')}</p>
      </StatusNote>
    </section>
  );
}
